import { AuthSubscriptions } from '@hefe/auth';
import { t } from '@hefe/i18n';
import {
  BasketballTeamsSlice,
  BasketballUsersSlice,
  ProspectsApi,
  currentUserSelector,
  usePrevious,
  userActiveBasketballCoachTeamId,
} from '@hefe/store';
import { clubHasSubscription } from '@hefe/utils';
import { Badge, Row, Spin, Tabs, TabsProps, Typography } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../../store/store';
import TeamPlayersComponent from '../../../admin/components/teams/team-players/team-players.component';
import ProspectsComponent from '../../../users/components/prospects/prospects.component';

export default function TeamPlayersPage() {
  const dispatch = useAppDispatch();

  const activeTeamId = useSelector(userActiveBasketballCoachTeamId);
  const prevActiveCoachId = usePrevious(activeTeamId);
  const user = useSelector(currentUserSelector);

  const team = useSelector(
    BasketballTeamsSlice.basketballTeamSelector(activeTeamId ?? 0)
  );

  const [loading, setLoading] = useState(false);

  const { data, isFetching } = ProspectsApi.useFetchProspectsQuery(
    team ? team.id + '' : ''
  );

  const prospectsAllowed = clubHasSubscription(AuthSubscriptions.third, user);

  useEffect(() => {
    fetchTeam();
    fetchAllUsers();
  }, []);

  useEffect(() => {
    if (prevActiveCoachId !== undefined && activeTeamId !== prevActiveCoachId) {
      fetchTeam();
      fetchAllUsers();
    }
  }, [activeTeamId]);

  const fetchTeam = () => {
    if (!activeTeamId) {
      return;
    }

    setLoading(true);
    dispatch(BasketballTeamsSlice.fetchBasketballTeam(activeTeamId))
      .unwrap()
      .finally(() => setLoading(false));
  };

  const fetchAllUsers = () => {
    dispatch(
      BasketballUsersSlice.fetchBasketballUsersSelect({
        cacheDuration: 5 * 60 * 1000,
      })
    )
      .unwrap()
      .catch(() => null);
  };

  const pendingProspectsCount = useMemo(() => {
    return (
      data?.filter((prospect) => prospect.status === 'pending').length ?? 0
    );
  }, [data]);

  const items = useMemo(() => {
    const returnValue: TabsProps['items'] = [];

    returnValue.push({
      key: 'users',
      label: t('players'),
      children: team ? <TeamPlayersComponent team={team} /> : undefined,
    });

    if (prospectsAllowed) {
      returnValue.push({
        key: 'prospects',
        label: (
          <Badge count={pendingProspectsCount} offset={[10, 0]}>
            <Typography.Text>{t('prospects.plural')}</Typography.Text>
          </Badge>
        ),
        children: (
          <ProspectsComponent prospects={data ?? []} isFetching={isFetching} />
        ),
      });
    }

    return returnValue;
  }, [data, isFetching, pendingProspectsCount, prospectsAllowed, team]);

  if (loading) {
    return (
      <Row align={'middle'} justify={'center'} style={{ height: '100%' }}>
        <Spin size={'large'} />
      </Row>
    );
  }

  if (team) {
    return <Tabs defaultActiveKey="players" items={items} />;
  } else {
    return null;
  }
}
